// Renders the skill tags the captions Lambda already extracts (skillCategory,
// difficulty, techniques, freeTags). Used on VideoCard and VideoDetail
// header. Editorial aesthetic: thin outline chips, small caps for the
// category, lowercased for techniques (matches the way the LLM emits them).

import React from "react";

interface Props {
  skillCategory: string | null;
  difficulty: string | null;
  techniques: string[];
  freeTags?: string[];
  /** "card" is compact (≤2 lines, max 3 techniques). "detail" shows all. */
  variant?: "card" | "detail";
}

// Category dictates the accent. Subtle — these are status hints, not banners.
const CATEGORY_ACCENT: Record<string, string> = {
  Technique: "border-orange-500/40 text-orange-200/90",
  Theory: "border-amber-200/30 text-amber-100/90",
  Songs: "border-emerald-400/30 text-emerald-200/90",
};

const DIFFICULTY_DOT: Record<string, string> = {
  beginner: "bg-emerald-400",
  intermediate: "bg-amber-300",
  advanced: "bg-orange-400",
};

function CategoryChip({ category }: { category: string }) {
  const accent =
    CATEGORY_ACCENT[category] ?? "border-gray-600 text-gray-300";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] ${accent}`}
    >
      {category}
    </span>
  );
}

function DifficultyChip({ difficulty }: { difficulty: string }) {
  const dot = DIFFICULTY_DOT[difficulty] ?? "bg-gray-400";
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-300">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden />
      <span className="font-display-italic">{difficulty}</span>
    </span>
  );
}

function TechniqueChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-gray-700/80 bg-gray-900/30 px-2 py-0.5 text-[11px] text-gray-300">
      {label}
    </span>
  );
}

export default function SkillBadges({
  skillCategory,
  difficulty,
  techniques,
  freeTags,
  variant = "card",
}: Props) {
  const compact = variant === "card";
  const maxTechniques = compact ? 3 : 12;
  const visibleTechniques = techniques.slice(0, maxTechniques);
  const overflowCount = techniques.length - visibleTechniques.length;
  const visibleFreeTags =
    !compact && freeTags ? freeTags.slice(0, 4) : [];

  const hasContent =
    skillCategory || difficulty || visibleTechniques.length > 0 ||
    visibleFreeTags.length > 0;
  if (!hasContent) return null;

  return (
    <div
      data-testid="skill-badges"
      className={`flex flex-wrap items-center gap-x-2 gap-y-1.5 ${
        compact ? "" : "gap-x-3"
      }`}
    >
      {skillCategory ? <CategoryChip category={skillCategory} /> : null}
      {difficulty ? <DifficultyChip difficulty={difficulty} /> : null}
      {visibleTechniques.map((t) => (
        <TechniqueChip key={t} label={t} />
      ))}
      {overflowCount > 0 ? (
        <span className="text-[11px] text-gray-500 font-display-italic">
          +{overflowCount} more
        </span>
      ) : null}
      {visibleFreeTags.map((t) => (
        <span
          key={t}
          className="text-[11px] text-gray-500 font-display-italic"
        >
          {t}
        </span>
      ))}
    </div>
  );
}
